import { useState, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useCartStore }    from '../store/cartStore.js';
import { useSessionStore } from '../store/sessionStore.js';
import { useTerminalStore } from '../store/terminalStore.js';
import { useTabsStore }    from '../store/tabsStore.js';
import { waiterId }        from '../store/waiterStore.js';
import { usarPlan }        from '../lib/plan.js';
import { useTipsStore }    from '../store/tipsStore.js';
import { createPayment }   from '../api/pos.api.js';
import { markTabServed }   from '../api/kitchen.api.js';
import paymentTerminal     from '../native/paymentTerminal.js';
import { Capacitor }      from '@capacitor/core';
import { v4 as uuidv4 }    from 'uuid';
import { IcoCash, IcoCard, IcoTransfer, IcoMixed, IcoUser, IcoWarning, IcoCheck } from '../components/common/Icons.jsx';
import SplitBillSheet      from '../components/SplitBillSheet.jsx';
import styles from './CheckoutScreen.module.css';

const isNative = Capacitor.isNativePlatform();

const METHODS = [
  { key: 'cash',     label: 'Efectivo',      Icon: IcoCash     },
  { key: 'card',     label: 'Tarjeta',        Icon: IcoCard     },
  { key: 'transfer', label: 'Transferencia',  Icon: IcoTransfer },
  { key: 'mixed',    label: 'Mixto',          Icon: IcoMixed    },
];

const fmt = (n) => `$${Number(n).toLocaleString('es-MX', { minimumFractionDigits: 2 })}`;

export default function CheckoutScreen() {
  const navigate   = useNavigate();
  const session              = useSessionStore((s) => s.session);
  const closeSession         = useSessionStore((s) => s.closeSession);
  const updateSessionTotals  = useSessionStore((s) => s.updateSessionTotals);
  const customer   = useCartStore((s) => s.customer);
  const items      = useCartStore((s) => s.items);
  const discount   = useCartStore((s) => s.discount);
  const subtotal   = useCartStore((s) => s.getSubtotal());
  const saleTotal  = useCartStore((s) => s.getTotal());   // mercancía, sin propina
  const note       = useCartStore((s) => s.note);
  const clearCart  = useCartStore((s) => s.clearCart);

  const terminal       = useSessionStore((s) => s.terminal);
  const terminalStatus = useTerminalStore((s) => s.status);
  const activeTabId    = useTabsStore((s) => s.activeTabId);
  const closeTab       = useTabsStore((s) => s.closeTab);
  const clearActiveTab = useTabsStore((s) => s.clearActiveTab);
  const abortRef       = useRef(null);

  const [method,         setMethod]         = useState('cash');
  // Lo que el cajero anota del voucher de su terminal (autorización o últimos 4).
  const [cardRef,        setCardRef]        = useState('');
  const [showItems,      setShowItems]      = useState(false);
  const [amountCash,     setAmountCash]     = useState('');
  const [mixedCash,      setMixedCash]      = useState('');   // cash portion for mixed
  const [loading,        setLoading]        = useState(false);
  const [error,          setError]          = useState(null);
  const [terminalPhase,  setTerminalPhase]  = useState(null); // null | 'preparing' | 'waiting' | 'processing'

  // Un cobro puede fallar porque la caja ya se cerró en otro lado: desde el
  // panel, desde otra tablet, o porque la tablet llevaba días apagada. El POS
  // guarda la sesión y nadie le pregunta al servidor si sigue viva, así que se
  // entera aquí, con la venta ya capturada.
  //
  // En ese caso no sirve de nada dejarlo en esta pantalla: sin turno abierto no
  // va a poder cobrar por más que lo intente. Se le suelta la sesión muerta y
  // se le manda a abrir una — el carrito NO se toca, así que al volver la venta
  // sigue ahí y solo tiene que tocar Cobrar otra vez.
  const manejarError = (err, porDefecto) => {
    if (err.response?.data?.session_invalid) {
      // Soltar la sesión muerta basta: `SessionRoute` lo lleva a abrir turno.
      // El aviso viaja en el store, no en la navegación, justamente porque esa
      // redirección es de otro y pisaría el state.
      closeSession('La caja se cerró desde otro lado. Abre turno para cobrar: la venta sigue capturada.');
      return;
    }
    setError(err.response?.data?.error ?? porDefecto);
  };

  // ── Propina ──────────────────────────────────────────────────────────────
  // Hace falta que el plan traiga propinas Y que este aparato las pida.
  const plan        = usarPlan();
  const tipsEnabled = useTipsStore((s) => s.enabled) && plan.propinas;
  const tipPresets  = useTipsStore((s) => s.presets);
  const [tipPct,    setTipPct]    = useState(null);  // porcentaje elegido, o null
  const [tipCustom, setTipCustom] = useState('');    // importe manual

  // El porcentaje se calcula sobre la mercancía ya con descuento aplicado.
  const tip = !tipsEnabled
    ? 0
    : tipPct != null
      ? Math.round(saleTotal * (tipPct / 100) * 100) / 100
      : Math.max(0, Number(tipCustom) || 0);

  // Lo que realmente se cobra al cliente
  const total = Math.round((saleTotal + tip) * 100) / 100;

  const overStockItems = items.filter(
    (i) => i.product.stock != null && i.quantity > i.product.stock,
  );

  const provider = terminal?.terminal_provider ?? 'stripe';
  const needsNative = provider === 'stripe';
  // Terminal propia del negocio, sin conexión con el sistema: no hay lector que
  // esperar. El cajero cobra en ella y aquí solo se registra el pago.
  const terminalPropia = provider === 'external';

  const changeDue = method === 'cash' && amountCash
    ? Math.max(0, Number(amountCash) - total)
    : 0;

  // mixed: card = total - cash
  const mixedCashNum  = Math.min(Math.max(0, Number(mixedCash) || 0), total);
  const mixedCardNum  = Math.max(0, total - mixedCashNum);
  const mixedValid    = method === 'mixed' && mixedCashNum > 0 && mixedCardNum > 0;

  const buildItems = () => items.map((i) => ({
    product_id: i.product._id,
    name:       i.product.name,
    quantity:   i.quantity,
    unit_price: i.unit_price,
    // Se guarda con la venta para que el ticket reimpreso meses después traiga
    // el mismo desglose que el que se entregó en la mesa.
    seats:      i.seats ?? [],
    modifiers:  (i.modifiers ?? []).map((m) => ({
      group_name:  m.group_name,
      option_name: m.option_name,
      price_delta: m.price_delta ?? 0,
    })),
  }));

  const buildCfdiItems = () => items.map((i) => ({
    name:            i.product.name,
    quantity:        i.quantity,
    unit_price:      i.unit_price,
    // Para agrupar el ticket por comensal, como la comanda de cocina.
    seats:           i.seats ?? [],
    modifiers:       i.modifiers ?? [],
    original_price:  i.product.price !== i.unit_price && !(i.modifiers?.length) ? i.product.price : undefined,
    clave_prod_serv: i.product.clave_prod_serv ?? null,
    clave_unidad:    i.product.clave_unidad    ?? 'H87',
  }));

  const finishPayment = (payment, { client_uuid, split = null }) => {
    // Se toma AQUÍ y no al armar el ticket: más abajo `clearCart()` deja el
    // carrito vacío.
    //
    // Con la cuenta dividida SÍ va, siempre que se haya dividido por consumo:
    // ahí el desglose explica de dónde salió cada monto, que es justo lo que el
    // cliente pregunta. Antes se quitaba en toda división y el caso donde más
    // falta hacía —cada quien paga lo suyo— era precisamente el que se quedaba
    // sin él. Lo que sí confunde es ponerlo debajo de una división PAREJA:
    // serían cifras que no coinciden con lo que nadie pagó.
    const desglose = !split || split.modo === 'consumo' ? porComensal : null;
    // Con cuenta dividida el efectivo que entra es la suma de las partes que
    // se pagaron en efectivo, no el total: el resto fue tarjeta o transferencia.
    const efectivoEntrante = split
      ? split.paid.reduce((acc, p) => acc + (p.method === 'cash' ? p.amount : 0), 0)
      : (method === 'cash' ? total : 0);

    // sales_total lleva solo mercancía; expected_cash sí incluye la propina en
    // efectivo porque ese dinero entra al cajón. La venta cuenta como UNA
    // aunque la hayan pagado entre varios.
    updateSessionTotals({
      sales_count:   (session.sales_count   ?? 0) + 1,
      sales_total:   (session.sales_total   ?? 0) + saleTotal,
      tips_total:    (session.tips_total    ?? 0) + tip,
      expected_cash: (session.expected_cash ?? 0) + efectivoEntrante,
    });
    // Cobrada la cuenta, lo que siga abierto en cocina se da por entregado —
    // si no, la pantalla de cocina se llena de mesas que ya se fueron.
    if (activeTabId) {
      if (plan.cocina) markTabServed(activeTabId).catch(() => {});
      // Cerrar la cuenta en la caja libera la mesa y la quita de las demás
      // tablets. Va sin await: el mesero no debe esperar a la red para cobrar.
      closeTab(activeTabId, { reason: 'paid', payment_client_uuid: client_uuid }).catch(() => {});
      clearActiveTab();
    }
    clearCart();
    navigate('/receipt', {
      state: {
        total: saleTotal, tip, grandTotal: total,
        subtotal, discount,
        method:    split ? 'split' : method,
        splitCount: split?.count ?? null,
        changeDue: split ? 0 : changeDue,
        client_uuid,
        paymentId: payment?._id ?? null, cfdiItems: buildCfdiItems(), note, customer,
        porComensal: desglose,
      },
      replace: true,
    });
  };

  // ── Cuenta dividida ───────────────────────────────────────────────────────
  // Lo que le toca a cada comensal, si el mesero los capturó. La hoja de
  // división lo usa para arrancar en "por consumo" en vez de partir parejo.
  // Los apodos viajan con el pago para que el ticket reimpreso y el recibo por
  // correo digan lo mismo que el que se entregó en la mesa. Se leen antes de
  // cobrar: `clearCart()` los borra junto con el carrito.
  const comensalesDeLaMesa = useCartStore.getState().comensales;

  const porComensal = useCartStore.getState().seatsMesa().length > 1
    ? useCartStore.getState().totalPorComensal()
    : [];

  const [splitOpen,  setSplitOpen]  = useState(false);
  const [splitPaid,  setSplitPaid]  = useState([]);     // [{amount, method}] por parte
  const [splitBusy,  setSplitBusy]  = useState(null);
  const splitGroupRef = useRef(null);

  // La propina es de quien atendió la mesa, no de quien cobra. Se manda la
  // cuenta y el servidor resuelve el mesero desde ella; sin cuenta —venta de
  // mostrador— cae en el usuario del token, que es quien está cobrando.
  const datosCuenta = () => {
    const cuenta = useTabsStore.getState().tabs.find((t) => t.id === activeTabId);
    return {
      tab_id:    activeTabId ?? null,
      tab_name:  cuenta?.name ?? null,
      // Quien abrió la cuenta manda sobre quien está en turno: si Ana levantó
      // la mesa y Luis la cobra al final, la propina sigue siendo de Ana.
      waiter_id: cuenta?.waiter?._id ?? waiterId() ?? null,
    };
  };

  /**
   * Cobra una parte de la cuenta dividida.
   *
   * Cada parte es su propio PosPayment: puede pagar con método distinto y
   * pedir su propia factura. El backend solo descuenta inventario y genera el
   * pedido desde la parte 0, así que la venta sigue siendo una.
   *
   * La propina y la mercancía se reparten en proporción al monto de la parte,
   * para que sales_total y tips_total sigan cuadrando al sumar todas.
   */
  const handleChargeSplit = async ({ index, amount, method: partMethod, count, modo }) => {
    setError(null);
    setSplitBusy(index);

    if (!splitGroupRef.current) splitGroupRef.current = uuidv4();

    // Cada parte redondea su tramo de propina, y esos redondeos no suman
    // exactamente el total. La última parte en cobrarse absorbe el resto, para
    // que sales_total y tips_total cuadren al centavo. Se detecta por cuántas
    // faltan y no por el índice, porque el cajero puede cobrarlas en desorden.
    const r2         = (n) => Math.round(n * 100) / 100;
    const yaCobradas = splitPaid.filter(Boolean);
    const esUltima   = yaCobradas.length === count - 1;

    const tipAsignado = yaCobradas.reduce((acc, p) => acc + (p.tip ?? 0), 0);
    const parteTip    = esUltima
      ? r2(tip - tipAsignado)
      : r2(tip * (total > 0 ? amount / total : 0));
    const parteVenta  = r2(amount - parteTip);

    try {
      const client_uuid = uuidv4();
      await createPayment({
        client_uuid,
        ...datosCuenta(),
        session_id:      session._id,
        method:          partMethod,
        total:           parteVenta,
        tip:             parteTip,
        // El descuento ya está reflejado en el total repartido; cargarlo en
        // cada parte lo contaría N veces en el pedido.
        discount:        index === 0 ? (discount || 0) : 0,
        amount_cash:     partMethod === 'cash'     ? amount : 0,
        amount_card:     partMethod === 'card'     ? amount : 0,
        amount_transfer: partMethod === 'transfer' ? amount : 0,
        change_given:    0,
        customer_id:     customer?._id ?? null,
        items:           buildItems(),
        comensales:      comensalesDeLaMesa,
        note:            note || null,
        split_group:     splitGroupRef.current,
        split_index:     index,
        split_count:     count,
      });

      const next = [...splitPaid];
      next[index] = { amount, method: partMethod, tip: parteTip, venta: parteVenta };
      setSplitPaid(next);

      // Cuando todas las partes están cobradas la venta se cierra igual que
      // una normal — el recibo muestra el total completo.
      const completas = Array.from({ length: count }, (_, i) => next[i]).every(Boolean);
      if (completas) {
        setSplitOpen(false);
        finishPayment(null, {
          client_uuid: splitGroupRef.current,
          split: { count, paid: next, modo },
        });
      }
    } catch (err) {
      manejarError(err, 'No se pudo cobrar esta parte');
    } finally {
      setSplitBusy(null);
    }
  };

  const handleCharge = async () => {
    setError(null);

    // ── Tarjeta con terminal propia: ya se cobró afuera, solo se registra ────
    // No hay lector que confirme el cargo: el botón dice que se cobró y el
    // cajero lo confirma al tocarlo. Funciona igual sin internet.
    if ((method === 'card' || method === 'mixed') && terminalPropia) {
      const cardAmount = method === 'mixed' ? mixedCardNum : total;
      setLoading(true);
      try {
        const client_uuid = uuidv4();
        const payment = await createPayment({
          client_uuid,
          ...datosCuenta(),
          session_id:      session._id,
          method,
          total:           saleTotal,
          tip,
          discount:        discount || 0,
          amount_cash:     method === 'mixed' ? mixedCashNum : 0,
          amount_card:     cardAmount,
          change_given:    0,
          card_reference:  cardRef.trim() || null,
          customer_id:     customer?._id ?? null,
          items:           buildItems(),
          comensales:      comensalesDeLaMesa,
          note:            note || null,
        });
        finishPayment(payment, { client_uuid });
      } catch (err) {
        manejarError(err, 'No se pudo registrar el pago con tarjeta');
      } finally {
        setLoading(false);
      }
      return;
    }

    // ── Tarjeta / Mixto-card: flujo terminal ────────────────────────────────
    if (method === 'card' || method === 'mixed') {
      const cardAmount = method === 'mixed' ? mixedCardNum : total;

      if (needsNative && !isNative) {
        setError('El cobro con tarjeta Stripe requiere el dispositivo físico (iPad/Android)');
        return;
      }
      if (provider === 'stripe' && terminalStatus !== 'connected') {
        setError('El lector de tarjetas no está conectado. Conéctalo desde el menú de configuración.');
        return;
      }

      abortRef.current = new AbortController();
      setLoading(true);
      try {
        const client_uuid = uuidv4();
        const { providerPaymentId } = await paymentTerminal.charge({
          amount:      cardAmount,
          terminal,
          externalRef: client_uuid,
          signal:      abortRef.current.signal,
          onPhase:     setTerminalPhase,
        });

        const isStripe = provider === 'stripe';
        const payment = await createPayment({
          client_uuid,
          ...datosCuenta(),
          session_id:               session._id,
          method,
          total:                    saleTotal,
          tip,
          discount:                 discount || 0,
          amount_cash:              method === 'mixed' ? mixedCashNum : 0,
          amount_card:              cardAmount,
          change_given:             0,
          customer_id:              customer?._id ?? null,
          stripe_payment_intent_id: isStripe ? providerPaymentId : null,
          mp_payment_id:            !isStripe ? providerPaymentId : null,
          items:                    buildItems(),
          comensales:               comensalesDeLaMesa,
          note:                     note || null,
        });
        finishPayment(payment, { client_uuid });
      } catch (err) {
        if (err.message?.toLowerCase().includes('cancel')) setError('Cobro cancelado');
        else manejarError(err, err.message ?? 'Error al procesar la tarjeta');
      } finally {
        setLoading(false);
        setTerminalPhase(null);
        abortRef.current = null;
      }
      return;
    }

    // ── Efectivo / transferencia ─────────────────────────────────────────────
    setLoading(true);
    try {
      const client_uuid = uuidv4();
      const payment = await createPayment({
        client_uuid,
        ...datosCuenta(),
        session_id:      session._id,
        method,
        total:           saleTotal,
        tip,
        discount:        discount || 0,
        amount_cash:     method === 'cash'     ? total : 0,
        amount_card:     0,
        amount_transfer: method === 'transfer' ? total : 0,
        change_given:    changeDue,
        customer_id:     customer?._id ?? null,
        items:           buildItems(),
        comensales:      comensalesDeLaMesa,
        note:            note || null,
      });
      finishPayment(payment, { client_uuid });
    } catch (err) {
      manejarError(err, 'Error al procesar el pago');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className={styles.root}>
      <div className={styles.inner}>
        {/* ── Header ─────────────────────────────────────────────────── */}
        <div className={styles.header}>
          <button className="btn btn--ghost" onClick={() => navigate(-1)}>← Volver</button>
          <h1 className={styles.title}>Cobrar</h1>
        </div>

        {/* En tablet horizontal, dos columnas: la cuenta a la izquierda y el
            cobro a la derecha, siempre a la vista. Era una columna de 480 px
            con 400 px muertos por lado y Cobrar bajo el pliegue a 960×600. */}
        <div className={styles.cols}>
        <div className={styles.colIzq}>
        {/* ── Total ──────────────────────────────────────────────────── */}
        <div className={styles.totalBlock}>
          <p className={styles.totalLabel}>Total a cobrar</p>
          <p className={styles.totalAmount}>{fmt(total)}</p>
          {discount > 0 && (
            <p className={styles.discountLine}>Subtotal {fmt(subtotal)} · Descuento −{fmt(discount)}</p>
          )}
          {tip > 0 && (
            <p className={styles.discountLine}>Mercancía {fmt(saleTotal)} · Propina +{fmt(tip)}</p>
          )}
          {customer && (
            <p className={styles.customerChip}><IcoUser size={13} /> {customer.name}</p>
          )}
        </div>

        {/* ── Resumen de artículos ───────────────────────────────────── */}
        <button className={styles.itemsToggle} onClick={() => setShowItems((v) => !v)}>
          <span>{items.length} artículo{items.length !== 1 ? 's' : ''}</span>
          <span className={styles.itemsToggleArrow}>{showItems ? '▲' : '▼'}</span>
        </button>
        {showItems && (
          <div className={styles.itemsList}>
            {items.map((i) => (
              <div key={i.lineId} className={styles.itemsRow}>
                <span className={styles.itemsQty}>{i.quantity}×</span>
                <span className={styles.itemsName}>
                  {i.product.name}
                  {i.modifiers?.length > 0 && (
                    <span className={styles.itemsMods}>{i.modifiers.map((m) => m.option_name).join(' · ')}</span>
                  )}
                </span>
                <span className={styles.itemsTotal}>{fmt(i.total)}</span>
              </div>
            ))}
          </div>
        )}

        {/* ── Aviso de stock ─────────────────────────────────────────── */}
        {overStockItems.length > 0 && (
          <div className={styles.stockWarn}>
            <p className={styles.stockWarnTitle}><IcoWarning size={14} /> Stock insuficiente</p>
            {overStockItems.map((i) => (
              <p key={i.lineId} className={styles.stockWarnItem}>
                {i.product.name}: pedido {i.quantity}, disponible {i.product.stock ?? 0}
              </p>
            ))}
            <p className={styles.stockWarnNote}>Puedes continuar si autorizas la venta.</p>
          </div>
        )}

        {/* ── Propina ────────────────────────────────────────────────── */}
        {tipsEnabled && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Propina</p>
            <div className={styles.tipGrid}>
              <button
                className={`${styles.tipBtn} ${tipPct === null && tipCustom === '' ? styles.tipBtnActive : ''}`}
                onClick={() => { setTipPct(null); setTipCustom(''); }}
              >
                Sin propina
              </button>
              {tipPresets.map((p) => (
                <button
                  key={p}
                  className={`${styles.tipBtn} ${tipPct === p ? styles.tipBtnActive : ''}`}
                  onClick={() => { setTipPct(p); setTipCustom(''); }}
                >
                  <span className={styles.tipPct}>{p}%</span>
                  <span className={styles.tipAmt}>{fmt(saleTotal * (p / 100))}</span>
                </button>
              ))}
            </div>
            <div className={styles.currencyRow} style={{ marginTop: 8 }}>
              <span className={styles.currencySymbol}>$</span>
              <input
                className="input"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                placeholder="Otro monto"
                value={tipCustom}
                onChange={(e) => { setTipCustom(e.target.value); setTipPct(null); }}
                style={{ paddingLeft: 32 }}
              />
            </div>
            {tip > 0 && (
              <div className={styles.tipSummary}>
                <span>Mercancía {fmt(saleTotal)} · Propina {fmt(tip)}</span>
                <strong>Total {fmt(total)}</strong>
              </div>
            )}
          </div>
        )}

        {/* ── Dividir cuenta ─────────────────────────────────────────── */}
        {plan.dividirCuenta && (
          <button
            className={styles.splitLink}
            onClick={() => setSplitOpen(true)}
            disabled={loading || total <= 0}
          >
            <IcoUser size={16} /> Dividir cuenta entre varias personas
          </button>
        )}

        </div>
        <div className={styles.colDer}>
        {/* ── Forma de pago ──────────────────────────────────────────── */}
        <div className={styles.section}>
          <p className={styles.sectionLabel}>Forma de pago</p>
          <div className={styles.methodGrid}>
            {METHODS.map((m) => (
              <button
                key={m.key}
                className={`${styles.methodBtn} ${method === m.key ? styles.methodBtnActive : ''}`}
                onClick={() => setMethod(m.key)}
              >
                <span className={styles.methodIcon}><m.Icon size={20} /></span>
                <span className={styles.methodLabel}>{m.label}</span>
              </button>
            ))}
          </div>
        </div>

        {/* ── Pago mixto ─────────────────────────────────────────────── */}
        {method === 'mixed' && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Divide el pago</p>
            <div className={styles.currencyRow}>
              <span className={styles.currencySymbol}>$</span>
              <input
                className="input"
                type="number"
                inputMode="decimal"
                min={0}
                max={total}
                step="0.01"
                placeholder="Monto en efectivo"
                value={mixedCash}
                onChange={(e) => setMixedCash(e.target.value)}
                style={{ paddingLeft: 32, fontSize: 18 }}
                autoFocus
              />
            </div>
            {mixedCash !== '' && (
              <div className={styles.mixedSplit}>
                <div className={styles.mixedRow}>
                  <span><IcoCash size={14} /> Efectivo</span>
                  <strong>{fmt(mixedCashNum)}</strong>
                </div>
                <div className={styles.mixedRow}>
                  <span><IcoCard size={14} /> {terminalPropia ? 'Tarjeta (tu terminal)' : 'Tarjeta (lector)'}</span>
                  <strong>{fmt(mixedCardNum)}</strong>
                </div>
              </div>
            )}
          </div>
        )}

        {/* ── Terminal propia: cobrar afuera y registrar aquí ──────────── */}
        {(method === 'card' || method === 'mixed') && terminalPropia && (
          <div className={styles.section}>
            <p className={styles.terminalPropia}>
              Cobra <strong>{fmt(method === 'mixed' ? mixedCardNum : total)}</strong> en tu terminal.
              Cuando salga aprobado, regístralo aquí.
            </p>
            <input
              className="input"
              type="text"
              inputMode="text"
              maxLength={40}
              autoComplete="off"
              placeholder="Autorización o últimos 4 dígitos (opcional)"
              aria-label="Autorización o últimos 4 dígitos de la tarjeta"
              value={cardRef}
              onChange={(e) => setCardRef(e.target.value)}
            />
          </div>
        )}

        {/* ── Estado del lector (card o mixto con tarjeta) ───────────── */}
        {(method === 'card' || method === 'mixed') && !terminalPropia && (
          <div className={styles.terminalInfo}>
            {terminalPhase === 'waiting' ? (
              <p className={styles.terminalWaiting}>
                {provider === 'mercadopago'
                  ? 'Esperando confirmación en el lector MP...'
                  : 'Acercar tarjeta al lector...'}
              </p>
            ) : terminalPhase === 'preparing' ? (
              <p className={styles.terminalStatus}>Preparando terminal...</p>
            ) : terminalPhase === 'processing' ? (
              <p className={styles.terminalStatus}>Procesando pago...</p>
            ) : provider === 'mercadopago' ? (
              <p className={styles.terminalOk}>Lector Mercado Pago configurado</p>
            ) : terminalStatus === 'connected' ? (
              <p className={styles.terminalOk}>Lector Stripe conectado</p>
            ) : (
              <p className={styles.terminalOff}>Lector no conectado — conéctalo en Configuración</p>
            )}
          </div>
        )}

        {/* ── Efectivo recibido (solo cash) ──────────────────────────── */}
        {method === 'cash' && (
          <div className={styles.section}>
            <p className={styles.sectionLabel}>Efectivo recibido</p>
            <div className={styles.currencyRow}>
              <span className={styles.currencySymbol}>$</span>
              <input
                className="input"
                type="number"
                inputMode="decimal"
                min={0}
                step="0.01"
                placeholder={total.toFixed(2)}
                value={amountCash}
                onChange={(e) => setAmountCash(e.target.value)}
                style={{ paddingLeft: 32, fontSize: 20 }}
                autoFocus
              />
            </div>
            {/* Denominaciones rápidas */}
            <div className={styles.denominations}>
              {(() => {
                const bills = [20, 50, 100, 200, 500, 1000];
                // Smallest bill that covers total, then the next 3 up
                const minIdx = bills.findIndex((b) => b >= total);
                const start  = minIdx === -1 ? bills.length - 1 : minIdx;
                const shown  = bills.slice(Math.max(0, start - 1), start + 4);
                return shown.map((d) => (
                  <button
                    key={d}
                    className={`${styles.denomBtn} ${Number(amountCash) === d ? styles.denomBtnActive : ''}`}
                    onClick={() => setAmountCash(String(d))}
                  >
                    ${d}
                  </button>
                ));
              })()}
              {/* Botón "exacto" */}
              <button
                className={`${styles.denomBtn} ${Number(amountCash) === total ? styles.denomBtnActive : ''}`}
                onClick={() => setAmountCash(total.toFixed(2))}
              >
                Exacto
              </button>
            </div>
            {/* Cambio a regresar */}
            {amountCash !== '' && (
              changeDue > 0 ? (
                <div className={styles.changeBox}>
                  <span className={styles.changeLabel}>Cambio a regresar</span>
                  <strong className={styles.changeAmount}>{fmt(changeDue)}</strong>
                </div>
              ) : Number(amountCash) < total ? (
                <div className={styles.changeShort}>
                  <span>Faltan</span>
                  <strong>{fmt(total - Number(amountCash))}</strong>
                </div>
              ) : (
                <div className={styles.changeExact}>
                  <span><IcoCheck size={13} /> Monto exacto</span>
                </div>
              )
            )}
          </div>
        )}

        {/* ── Error ──────────────────────────────────────────────────── */}
        {error && <p className={styles.error}>{error}</p>}

        {/* ── Botón cobrar / cancelar ────────────────────────────────── */}
        {loading && terminalPhase ? (
          <button
            className={`btn btn--danger btn--full ${styles.chargeBtn}`}
            onClick={() => abortRef.current?.abort()}
          >
            Cancelar cobro
          </button>
        ) : (
          <button
            className={`btn btn--primary btn--full ${styles.chargeBtn}`}
            onClick={handleCharge}
            disabled={
              loading ||
              items.length === 0 ||
              (method === 'cash' && amountCash !== '' && Number(amountCash) < total) ||
              (method === 'mixed' && !mixedValid)
            }
          >
            {loading
              ? <span className="spinner" />
              : (method === 'card' || method === 'mixed') && terminalPropia
                ? `Ya se cobró · registrar ${fmt(total)}`
                : `Cobrar ${fmt(total)}`}
          </button>
        )}
        </div>
        </div>
      </div>

      {splitOpen && (
        <SplitBillSheet
          total={total}
          porComensal={porComensal}
          paid={splitPaid}
          busyIndex={splitBusy}
          onCharge={handleChargeSplit}
          onClose={() => setSplitOpen(false)}
        />
      )}
    </div>
  );
}
